import React, { useEffect, useState } from 'react';
import { supabaseAuth } from '../lib/supabaseClient';
import { getHubToken } from '../lib/hubAuth';
import { BACKEND_URL } from '../lib/api';

const ROLE_HIERARCHY = { admin: 4, owner: 3, member: 2, customer: 1 };

function checkRoleAccess(requiredRole, authContext) {
  if (!requiredRole) return true;
  if (authContext.isPlatformAdmin) return true;
  
  const effectiveRole = authContext.company_role || authContext.role || 'customer';
  const userLevel = ROLE_HIERARCHY[effectiveRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
}

export default function ProtectedRoute({ children, requiredRole }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();

    const { data: { subscription } } = supabaseAuth.auth.onAuthStateChange((_event, session) => {
      if (getHubToken()) {
        setAuthenticated(true);
      } else {
        setAuthenticated(!!session);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = async () => {
    try {
      // Check 1: Hub token (from customer portal)
      const hubToken = getHubToken();
      if (hubToken) {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${hubToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.profile) localStorage.setItem('profile', JSON.stringify(data.profile));
          if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
          
          // Store auth context for role helpers
          const authContext = {
            isPlatformAdmin: data.user?.isPlatformAdmin || false,
            role: data.role,
            company_role: data.company_role,
            company: data.company,
            company_memberships: data.company_memberships,
          };
          localStorage.setItem('authContext', JSON.stringify(authContext));
          window.dispatchEvent(new Event('authContextUpdated'));

          if (!checkRoleAccess(requiredRole, authContext)) {
            setAuthenticated(false);
            setLoading(false);
            return;
          }

          setAuthenticated(true);
          setLoading(false);
          return;
        }
      }

      // Check 2: Local Supabase session (direct login)
      const { data: { session } } = await supabaseAuth.auth.getSession();

      if (session) {
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt && expiresAt - now < 60) {
          await supabaseAuth.auth.refreshSession();
        }

        // Role check using stored auth context
        if (requiredRole) {
          const authContext = JSON.parse(localStorage.getItem('authContext') || '{}');
          if (!checkRoleAccess(requiredRole, authContext)) {
            setAuthenticated(false);
            setLoading(false);
            return;
          }
        }

        setAuthenticated(true);
      } else {
        setAuthenticated(false);
      }
    } catch {
      setAuthenticated(false);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!authenticated) {
    window.location.replace('https://customer-portal-447.pages.dev/login');
    return null;
  }

  return children;
}
